import { AlertTriangle } from "lucide-react";

/**
 * Central registry of industry-specific regulatory / delivery notices.
 * Used on both the Project Builder (during scope entry) and the Proposal
 * page (once the AI has produced a scope). Kept out of the two page files
 * so the exact wording stays consistent across both surfaces.
 */
export const INDUSTRY_NOTICES = {
  Healthcare: {
    color: "red",
    title: "Healthcare Regulatory Notice",
    points: [
      "Software for medical diagnosis, monitoring, or therapy may qualify as a Medical Device (SaMD) under EU MDR 2017/745, requiring CE marking and Notified Body assessment.",
      "Health data is a special category under GDPR Article 9, requiring explicit legal basis, DPO appointment (large-scale), and DPIA.",
      "AI used in clinical decisions is High-Risk under the EU AI Act (Annex III), requiring conformity assessments.",
      "US clients: HIPAA Business Associate Agreements required for any PHI features.",
      "FastPrototype.ai MVPs are NOT MDR-certified. Consult a regulatory affairs specialist before commercialisation.",
    ],
  },
  Aviation: {
    color: "amber",
    title: "Aviation Regulatory Notice",
    points: [
      "Safety-critical aviation software requires DO-178C/EASA certification with Development Assurance Levels (DAL A–E). FastPrototype.ai does NOT deliver DO-178C-certified software.",
      "FastPrototype.ai is suitable for non-safety functions: crew scheduling, cost-sharing, booking, logistics, analytics, and operational tools.",
      "EASA regulations (EU 748/2012) govern initial airworthiness. Software affecting aircraft airworthiness MUST NOT be based on a FastPrototype.ai MVP.",
      "Consult an EASA-approved Design Organisation before developing any safety-critical aviation software.",
    ],
  },
  Finance: {
    color: "blue",
    title: "Finance Regulatory Notice",
    points: [
      "DORA (EU 2022/2554) applies to financial entities from January 17, 2025. ICT third-party risk management and TLPT requirements apply to software used by regulated entities.",
      "Investment platforms require MiFID II compliance: risk warnings, KIDs, investor categorisation. FastPrototype.ai MVPs are not licensed financial services.",
      "Payment services require PSD2 licensing (SCA, open banking APIs). Obtain a Payment Institution or E-Money licence before going live.",
      "AML/KYC obligations (AMLD6) apply to payment and investment platforms — a legal requirement, not a technical feature.",
      "Consult a FinTech compliance lawyer and obtain required licences before launching any regulated financial service.",
    ],
  },
  Marketplace: {
    color: "purple",
    title: "Marketplace Regulatory Notice",
    points: [
      "EU Digital Services Act (DSA) requires seller verification, internal complaints systems, prohibition of dark patterns, and algorithm transparency.",
      "Consumer Rights Directive requires clear withdrawal rights and an electronic 'Cancel Contract' button (mandatory from June 19, 2026).",
      "EU Product Liability Directive (2024/2853, from Dec 2026): Platforms may have direct liability for defective third-party products facilitated.",
      "Age verification systems required for platforms enabling access to age-restricted content.",
      "Platforms with >45M EU monthly users face VLOP obligations: systemic risk assessments, independent audits, researcher data access.",
    ],
  },
  "E-commerce": {
    color: "green",
    title: "E-commerce Regulatory Notice",
    points: [
      "EU Consumer Rights Directive: 14-day right of withdrawal, pre-contractual information disclosure, and an electronic 'Cancel Contract' button (mandatory from June 19, 2026).",
      "Omnibus Directive (EU 2019/2161): Mandatory display of prior price for any 'reduced' offer, transparent ranking criteria, and disclosure of paid placement.",
      "GDPR + ePrivacy: Cookie consent must be granular and opt-in (no pre-ticked boxes); withdrawal must be as easy as granting.",
      "EU Product Liability Directive (2024/2853): Sellers and online platforms may bear strict liability for defective products from December 2026.",
      "Geo-blocking Regulation (EU 2018/302) prohibits unjustified discrimination of EU customers based on nationality or residence.",
      "VAT One-Stop-Shop (OSS) required for cross-border B2C sales >€10,000/yr within the EU.",
    ],
  },
  SaaS: {
    color: "blue",
    title: "SaaS Delivery Notice",
    points: [
      "GDPR + ePrivacy: any SaaS handling EU personal data needs a lawful basis, a data-processing agreement (DPA) with each customer, and granular cookie consent — pre-ticked boxes are not compliant.",
      "EU AI Act: SaaS embedding AI features must classify each use-case under the Act's risk tiers (Prohibited / High-risk / Limited-risk / Minimal) and comply with the transparency + logging obligations that apply.",
      "The FastPrototype.ai MVP delivers a working SaaS. Production hardening — SSO, SCIM, SOC 2 controls, audit logs, uptime SLA infrastructure — is the client's responsibility and is not part of the standard MVP scope.",
      "For paid B2B SaaS in the EU: check VAT One-Stop-Shop registration once cross-border revenue exceeds €10,000/yr.",
    ],
  },
  Education: {
    color: "purple",
    title: "Education / EdTech Regulatory Notice",
    points: [
      "GDPR Article 8 sets 16 as the default digital consent age in the EU (member states can lower to 13). Any EdTech targeting minors must implement age gating and verifiable parental consent below that threshold.",
      "US audiences: COPPA applies to services directed at children under 13 — verifiable parental consent, restricted data collection, and clear privacy disclosures are required.",
      "US audiences again: FERPA restricts sharing of student education records. Institutional buyers will expect a signed FERPA-compliant DPA.",
      "EU AI Act: AI used for education access, evaluation of learning outcomes, or monitoring of students is classified as High-Risk (Annex III) — conformity assessment applies before commercial deployment.",
      "Accessibility: WCAG 2.1 AA and the European Accessibility Act (in force June 28, 2025) apply to consumer-facing EdTech. Bake accessibility into the design brief.",
    ],
  },
  Other: {
    color: "amber",
    title: "General Regulatory & Delivery Notice",
    points: [
      "Every FastPrototype.ai delivery is a working MVP intended for controlled validation — not a production-hardened system for unrestricted commercial deployment. Regulatory obligations for your specific industry are the client's responsibility.",
      "GDPR + ePrivacy always apply if you process EU personal data. At minimum: lawful basis for processing, transparent privacy notice, granular cookie consent, and a route for data subject rights requests.",
      "The EU AI Act applies to any AI feature that touches the EU market. Classify your feature under the Act's risk tiers before launch.",
      "Consult an industry-specialised lawyer if your product touches regulated activity: health, finance, insurance, energy, telecoms, transport, critical infrastructure, or content moderation at scale.",
    ],
  },
};

const COLORS = {
  red: "bg-red-50 border-red-200 text-red-900",
  amber: "bg-amber-50 border-amber-200 text-amber-900",
  blue: "bg-blue-50 border-blue-200 text-blue-900",
  purple: "bg-purple-50 border-purple-200 text-purple-900",
  green: "bg-green-50 border-green-200 text-green-900",
};
const ICON_COLORS = { red: "text-red-600", amber: "text-amber-600", blue: "text-blue-600", purple: "text-purple-600", green: "text-green-600" };
const POINT_COLORS = { red: "text-red-800", amber: "text-amber-800", blue: "text-blue-800", purple: "text-purple-800", green: "text-green-800" };

/**
 * Render a per-industry compliance notice. Falls back to the "Other" notice
 * when the industry name isn't in the registry — every project shows *some*
 * notice, so the delivery boundary is never invisible.
 */
export default function IndustryNotice({ industry }) {
  const notice = INDUSTRY_NOTICES[industry] || INDUSTRY_NOTICES.Other;
  const key = INDUSTRY_NOTICES[industry] ? industry : "other";
  return (
    <div
      data-testid={`industry-notice-${key.toLowerCase()}`}
      className={`rounded-xl border p-4 ${COLORS[notice.color]}`}
    >
      <div className="flex items-start gap-2 mb-2">
        <AlertTriangle size={15} className={`${ICON_COLORS[notice.color]} flex-shrink-0 mt-0.5`} />
        <span className="font-semibold text-sm">{notice.title}</span>
      </div>
      <ul className="space-y-1.5 pl-1">
        {notice.points.map((p) => (
          <li key={p} className={`text-xs ${POINT_COLORS[notice.color]} flex items-start gap-1.5`}>
            <span className="mt-0.5 flex-shrink-0">•</span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <a
        href="/legal#industry"
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-block mt-3 text-xs font-semibold underline ${ICON_COLORS[notice.color]}`}
      >
        Full legal notice for this industry →
      </a>
    </div>
  );
}
